import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import axios from '../../utils/axios.config';
import { logger } from '../../utils/logger';

// API URL is now handled by axios.config.ts
const API_URL = '';

interface ProfileData {
  _id?: string;
  userId?: any;
  gender: string;
  dateOfBirth: string;
  height: number;
  religion: string;
  community?: string;
  caste?: string;
  subCaste?: string;
  gothra?: string;
  motherTongue?: string;
  maritalStatus: string;
  about?: string;
  bio?: string;
  city: string;
  state: string;
  country: string;
  education: {
    field: string;
    institution: string;
    level: string;
  };
  occupation: string;
  employmentType?: string;
  workLocation: string;
  annualIncome: string | number;
  photos: any[];
  profilePhoto?: string;
  
  // Family details
  family?: {
    fatherName?: string;
    fatherOccupation?: string;
    motherName?: string;
    motherOccupation?: string;
    siblings?: {
      brothers?: number;
      sisters?: number;
      marriedBrothers?: number;
      marriedSisters?: number;
    };
    familyType?: string;
    familyStatus?: string;
    familyValues?: string;
  };
  
  // Lifestyle
  diet?: string;
  smoking?: string;
  drinking?: string;
  
  // Horoscope
  horoscope?: {
    birthTime?: string;
    birthPlace?: string;
    manglik?: string;
    raashi?: string;
    nakshatra?: string;
  };
  
  // Additional fields
  interests?: string[];
  hobbies?: string[];
  
  // Partner preferences (if any)
  partnerPreferences?: any;
}

interface ProfileState {
  data: ProfileData | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  lastFetched: number | null;
  cacheValid: boolean;
}

const initialState: ProfileState = {
  data: null,
  loading: false,
  saving: false,
  error: null,
  lastFetched: null,
  cacheValid: false,
};

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

// Async thunks
export const fetchProfile = createAsyncThunk(
  'profile/fetch',
  async (forceRefresh: boolean = false, { getState, rejectWithValue }) => {
    try {
      const state = getState() as any;
      const profileState = state.profile;

      // Check if cache is still valid
      if (
        !forceRefresh &&
        profileState.cacheValid &&
        profileState.lastFetched &&
        Date.now() - profileState.lastFetched < CACHE_DURATION &&
        profileState.data !== null
      ) {
        logger.log('✅ Using cached profile data');
        return null; // Return null to indicate cached data is being used
      }

      logger.log('🔄 Fetching fresh profile data from API');
      const { data } = await axios.get('/profiles/me');
      
      if (data.success) {
        return data.data;
      }
      return rejectWithValue('Profile not found');
    } catch (error: any) {
      // Silently handle errors when backend not accessible
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        // Return null instead of rejecting to avoid error state
        return null;
      }
      if (error.response?.status === 404) {
        // Profile doesn't exist yet, return null
        return null;
      }
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch profile');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'profile/update',
  async (profileData: Partial<ProfileData>, { rejectWithValue }) => {
    try {
      const { data } = await axios.put('/profiles/me', profileData);
      
      if (data.success) {
        return data.data;
      }
      return rejectWithValue('Update failed');
    } catch (error: any) {
      // Handle network errors gracefully - silently
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        // Return the profile data as-is to simulate success
        return profileData;
      }
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update profile';
      return rejectWithValue(errorMessage);
    }
  }
);

export const uploadPhoto = createAsyncThunk(
  'profile/uploadPhoto',
  async ({ url, isProfile = false }: { url: string; isProfile?: boolean }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post('/profiles/photos', { url, isProfile });
      
      if (data.success) {
        return data.data;
      }
      return rejectWithValue('Upload failed');
    } catch (error: any) {
      // Handle network errors gracefully - silently
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        // Return simulated data
        return { url, isProfile };
      }
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to upload photo';
      return rejectWithValue(errorMessage);
    }
  }
);

export const uploadMultiplePhotos = createAsyncThunk(
  'profile/uploadMultiplePhotos',
  async ({ images, primaryIndex = 0 }: { images: string[]; primaryIndex?: number }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post('/profiles/upload-multiple-photos', { images, primaryIndex });
      
      if (data.success) {
        return data.data;
      }
      return rejectWithValue('Upload failed');
    } catch (error: any) {
      // Handle network errors gracefully
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        return { uploadedPhotos: [], failedPhotos: [], totalUploaded: 0, totalFailed: images.length };
      }
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to upload photos';
      return rejectWithValue(errorMessage);
    }
  }
);

export const setPrimaryPhoto = createAsyncThunk(
  'profile/setPrimaryPhoto',
  async (photoId: string, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`/profiles/photos/${photoId}/set-primary`);
      
      if (data.success) {
        return data.data;
      }
      return rejectWithValue('Failed to set primary photo');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to set primary photo';
      return rejectWithValue(errorMessage);
    }
  }
);

export const deletePhoto = createAsyncThunk(
  'profile/deletePhoto',
  async (photoId: string, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete(`/profiles/photos/${photoId}/delete`);
      
      if (data.success) {
        return data.data;
      }
      return rejectWithValue('Failed to delete photo');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete photo';
      return rejectWithValue(errorMessage);
    }
  }
);

// Slice
const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    clearProfileError: (state) => {
      state.error = null;
    },
    setProfile: (state, action: PayloadAction<ProfileData>) => {
      state.data = action.payload;
    },
    clearProfile: (state) => {
      state.data = null;
      state.loading = false;
      state.saving = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch profile
    builder.addCase(fetchProfile.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchProfile.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        state.data = action.payload;
        state.lastFetched = Date.now();
        state.cacheValid = true;
      }
      state.error = null;
    });
    builder.addCase(fetchProfile.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update profile
    builder.addCase(updateProfile.pending, (state) => {
      state.saving = true;
      state.error = null;
    });
    builder.addCase(updateProfile.fulfilled, (state, action) => {
      state.saving = false;
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(updateProfile.rejected, (state, action) => {
      state.saving = false;
      state.error = action.payload as string;
    });

    // Upload photo
    builder.addCase(uploadPhoto.pending, (state) => {
      state.saving = true;
    });
    builder.addCase(uploadPhoto.fulfilled, (state, action) => {
      state.saving = false;
      state.data = action.payload;
    });
    builder.addCase(uploadPhoto.rejected, (state, action) => {
      state.saving = false;
      state.error = action.payload as string;
    });

    // Upload multiple photos
    builder.addCase(uploadMultiplePhotos.pending, (state) => {
      state.saving = true;
    });
    builder.addCase(uploadMultiplePhotos.fulfilled, (state, action) => {
      state.saving = false;
      if (action.payload.profile) {
        state.data = action.payload.profile;
      }
    });
    builder.addCase(uploadMultiplePhotos.rejected, (state, action) => {
      state.saving = false;
      state.error = action.payload as string;
    });

    // Set primary photo
    builder.addCase(setPrimaryPhoto.pending, (state) => {
      state.saving = true;
    });
    builder.addCase(setPrimaryPhoto.fulfilled, (state, action) => {
      state.saving = false;
      state.data = action.payload;
    });
    builder.addCase(setPrimaryPhoto.rejected, (state, action) => {
      state.saving = false;
      state.error = action.payload as string;
    });

    // Delete photo
    builder.addCase(deletePhoto.pending, (state) => {
      state.saving = true;
    });
    builder.addCase(deletePhoto.fulfilled, (state, action) => {
      state.saving = false;
      state.data = action.payload;
    });
    builder.addCase(deletePhoto.rejected, (state, action) => {
      state.saving = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearProfileError, setProfile, clearProfile } = profileSlice.actions;
export default profileSlice.reducer;